import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, CalendarClock, ExternalLink } from "lucide-react";
import { RenewDialog } from "@/components/RenewDialog";
import { ExpiryTextOptIn } from "@/components/ExpiryTextOptIn";
import { RenewalStreak } from "@/components/RenewalStreak";
import { api } from "@/lib/api";

const tone = (n) => (n < 0 ? "text-red-700 bg-red-50 border-red-200" : n <= 30 ? "text-amber-800 bg-amber-50 border-amber-200" : "text-slate-600 bg-slate-50 border-slate-200");
const left = (n) => (n < 0 ? `Expired ${-n} day${n === -1 ? "" : "s"} ago` : n === 0 ? "Expires today" : `${n} day${n === 1 ? "" : "s"} left`);

export function RenewalsCard() {
  const [docs, setDocs] = useState([]);
  const [renew, setRenew] = useState(null);
  const load = () => api.get("/documents/renewals").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!docs.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 mt-6" data-testid="renewals-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2"><CalendarClock className="h-5 w-5 text-emerald-600" />Renewals coming up</h3>
        <Link to="/dashboard/calendar" className="text-sm text-emerald-700 hover:underline" data-testid="renewals-calendar-link">Calendar</Link>
      </div>
      <RenewalStreak reloadKey={docs.map((d) => d.doc_id).join()} />
      <ul className="divide-y divide-slate-100">
        {docs.map((d) => (
          <li key={d.doc_id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`renewal-${d.doc_id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{d.filename.replace(/\.[^.]+$/, "").replace(" (scan)", "")}</div>
              <div className="text-xs text-slate-500">Expires {new Date(`${d.expires_on}T12:00:00`).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</div>
            </div>
            <span className={`self-start sm:self-auto text-xs font-semibold border rounded-full px-2.5 py-1 ${tone(d.days_left)}`} data-testid={`renewal-days-${d.doc_id}`}>{left(d.days_left)}</span>
            {d.renew_url ? (
              <a href={d.renew_url} target="_blank" rel="noreferrer" className="text-sm text-emerald-700 font-medium hover:underline inline-flex items-center gap-1" data-testid={`renewal-link-${d.doc_id}`}>Renew <ExternalLink className="h-3.5 w-3.5" /></a>
            ) : (
              <Link to="/dashboard/documents" className="text-sm text-slate-600 hover:underline" data-testid={`renewal-vault-${d.doc_id}`}>View</Link>
            )}
            <button onClick={() => setRenew(d)} className="text-sm text-slate-700 font-medium hover:text-emerald-700 inline-flex items-center gap-1" data-testid={`renewal-done-${d.doc_id}`}><BadgeCheck className="h-4 w-4" />Renewed</button>
          </li>
        ))}
      </ul>
      <ExpiryTextOptIn />
      {renew && <RenewDialog doc={renew} onClose={() => setRenew(null)} onSaved={load} />}
    </div>
  );
}
